import { useState, type CSSProperties, type ReactNode } from 'react';
import clsx from 'clsx';

export type BreakdownItem = {
  // Stable identifier — also used as the matchable handle for the optional
  // controlled `hovered` state, so it must equal whatever the parent's
  // donut sets via `onHoverChange`.
  key: string;
  label: ReactNode;
  value: number;
  color: string;
  // Optional tone applied to the formatted $ value: 'neg' tints it red,
  // 'pos' tints it green. Used to surface status (overspend, profitable
  // category, etc.) without the caller having to re-render the cell.
  valueTone?: 'pos' | 'neg';
};

type Props = {
  items: BreakdownItem[];
  // Denominator for each row's % column. Keep this separate from the items
  // sum so callers can express "share of all categories" even when the
  // visible list filters some out.
  total: number;
  formatValue: (value: number) => string;
  // After this many items, the rest collapse behind a "Show all" toggle and
  // a fade-out gradient. Defaults to 6 — short enough to stay scannable,
  // long enough to cover most categories without truncation.
  collapseAfter?: number;
  // Optional controlled hover state. When set, non-hovered rows dim to 40%
  // opacity so the matching donut slice stays the focal point. Pass both
  // props or neither — passing only one disables hover tracking.
  hovered?: string | null;
  onHoverChange?: (key: string | null) => void;
};

export function BreakdownLegend({
  items,
  total,
  formatValue,
  collapseAfter = 6,
  hovered,
  onHoverChange,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const collapsible = items.length > collapseAfter;
  const collapsed = collapsible && !expanded;
  const visible = collapsed ? items.slice(0, collapseAfter) : items;
  const hiddenCount = items.length - collapseAfter;
  const hoverEnabled = onHoverChange !== undefined;

  return (
    <div className="flex flex-col text-sm">
      <div className="relative">
        {/*
         * Bottom fade for collapsed mode. Was previously a CSS mask-image on
         * this container, but masks clip the painted area of descendants —
         * including the row dots' box-shadow glows that extend horizontally
         * past the container via the rows' `-mx-2` overhang. A separate
         * gradient overlay achieves the same fade without touching children's
         * shadow paint, so glows render cleanly on both sides.
         */}
        {visible.map((item) => {
          const pct = total > 0 ? (item.value / total) * 100 : 0;
          const isFocused = hoverEnabled && hovered === item.key;
          const dimmed =
            hoverEnabled && hovered != null && !isFocused;
          return (
            <div
              key={item.key}
              onMouseEnter={() => onHoverChange?.(item.key)}
              onMouseLeave={() => onHoverChange?.(null)}
              className="flex items-center justify-between gap-3 px-2 py-1.5 -mx-2 rounded-md cursor-default"
              style={{
                // Driven from the parent's controlled `hovered` so a slice
                // hover (or a row hover) highlights the matching row, not
                // just direct row :hover.
                backgroundColor: isFocused ? 'var(--berg-overlay)' : 'transparent',
                opacity: dimmed ? 0.4 : 1,
                transition: 'opacity 0.18s ease, background-color 0.15s ease',
              }}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span
                  className="w-2 h-2 rounded-full flex-none dot-glow"
                  style={
                    {
                      backgroundColor: item.color,
                      '--dot-color': item.color,
                    } as CSSProperties
                  }
                />
                <span className="truncate text-fg">{item.label}</span>
              </div>
              <div className="flex items-center gap-1.5 flex-none tabular">
                <span
                  className={clsx(
                    'font-semibold',
                    item.valueTone === 'neg' && 'text-red',
                    item.valueTone === 'pos' && 'text-green',
                  )}
                >
                  {formatValue(item.value)}
                </span>
                <span className="text-fg-tertiary text-xs w-12 text-right">
                  {pct.toFixed(1)}%
                </span>
              </div>
            </div>
          );
        })}
        {collapsed && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-b from-transparent to-elevated"
          />
        )}
      </div>
      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 self-center px-3 py-1.5 text-xs font-medium text-fg-secondary hover:text-fg transition-colors rounded-md bg-overlay hover:bg-border"
        >
          {expanded ? 'Show less' : `Show ${hiddenCount} more`}
        </button>
      )}
    </div>
  );
}
